import { requisicaoAutenticada } from './http';
import type { PapelUsuario } from './jwt';

/**
 * Quem está logado e a quais empresas pertence — `GET /api/auth/eu`.
 *
 * ## Por que isto não sai do token
 *
 * O access token carrega só ids (`jwt-payload.ts`): um JWT é assinado, não
 * criptografado, e nome, e-mail e razão social são dado pessoal. O efeito
 * prático era o seletor de empresa (RN-603) exibir UUID — um rótulo que o
 * usuário não tem como reconhecer. A resposta é um endpoint, não um token mais
 * gordo.
 *
 * Espelha `Eu` de `apps/api/src/modules/auth/eu.service.ts`. O backend já filtra
 * empresa inativa e preserva a ordem dos vínculos do token.
 */

export interface EmpresaDoUsuario {
  readonly businessId: string;
  /** Razão social — `Business.legalName`. */
  readonly razaoSocial: string;
  /** Nome fantasia — `Business.name`. Nem toda empresa tem. */
  readonly nomeFantasia: string | null;
  readonly papeis: readonly PapelUsuario[];
}

export interface Eu {
  readonly accountId: string;
  readonly nome: string;
  readonly emailAddress: string;
  readonly isAdmin: boolean;
  readonly empresas: readonly EmpresaDoUsuario[];
  /**
   * Vínculos com empresa desativada, à parte. Opcional: resposta de uma API
   * anterior não traz o campo, e a tela segue só com as ativas.
   */
  readonly empresasDesativadas?: readonly EmpresaDoUsuario[];
}

/** A rota é da conta, não de uma empresa: sem header de empresa corrente. */
export async function carregarEu(sinal?: AbortSignal): Promise<Eu> {
  return requisicaoAutenticada<Eu>('/auth/eu', { sinal });
}

/**
 * Nome de exibição da empresa — D-009.
 *
 * Nome fantasia primeiro, razão social como reserva. A operadora tem razão
 * social "MECA" e nome fantasia "MS3 Capital": exibir a razão social mostraria
 * ao contribuinte um nome que ele não reconhece, e "MECA" não aparece em lugar
 * nenhum fora do banco.
 */
export function nomeDaEmpresa(empresa: EmpresaDoUsuario): string {
  const fantasia = empresa.nomeFantasia?.trim() ?? '';
  return fantasia === '' ? empresa.razaoSocial : fantasia;
}

/**
 * Razão social quando ela **acrescenta** informação ao nome exibido.
 *
 * Repetir "MS3 Capital / MS3 Capital" é ruído; mostrar "MECA" embaixo de "MS3
 * Capital" ajuda quem confere contrato ou nota fiscal.
 */
export function razaoSocialComplementar(empresa: EmpresaDoUsuario): string | null {
  const exibido = nomeDaEmpresa(empresa);
  return empresa.razaoSocial.trim() === exibido.trim() ? null : empresa.razaoSocial;
}
